"use client";

import React from "react";
import { cn } from "@/lib/utils";
import { data } from "@/data";

export type DownloadButtonVariant = "appstore" | "playstore";
export type DownloadButtonSize = "default" | "compact";

interface DownloadButtonProps {
  variant: DownloadButtonVariant;
  size?: DownloadButtonSize;
  /** Use single-line label (e.g., "Download" for navbar) */
  singleLine?: boolean;
  /** Custom single-line label text */
  label?: string;
  /** Additional className */
  className?: string;
}

const buttonLinks: Record<DownloadButtonVariant, string> = {
  appstore: data.herosection.buttonslinks.appstore,
  playstore: data.herosection.buttonslinks.playstore,
};

const buttonLabels: Record<DownloadButtonVariant, { small: string; large: string }> = {
  appstore: { small: "Download on the", large: "App Store" },
  playstore: { small: "Get it on", large: "Google Play" },
};

// Apple icon SVG
function AppleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
    </svg>
  );
}

// Play Store icon SVG
function PlayStoreIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
      <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.5,12.92 20.16,13.19L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
    </svg>
  );
}

export function DownloadButton({
  variant,
  size = "default",
  singleLine = false,
  label,
  className,
}: DownloadButtonProps) {
  const href = buttonLinks[variant];
  const labels = buttonLabels[variant];
  const Icon = variant === "appstore" ? AppleIcon : PlayStoreIcon;

  // Base styles shared by all buttons
  const baseStyles = cn(
    "inline-flex items-center gap-3",
    "rounded-[14px] font-semibold text-base",
    "text-decoration-none cursor-pointer",
    "transition-all duration-[250ms] ease-[cubic-bezier(0.4,0,0.2,1)]",
    // Size variants
    size === "default" && "px-7 py-4",
    size === "compact" && "px-5 py-3"
  );

  // Variant-specific styles
  const variantStyles = cn(
    // App Store (Primary) - dark slate
    variant === "appstore" && [
      "bg-[#0f172a] text-white",
      "shadow-[0_4px_12px_rgba(15,23,42,0.25),0_0_0_0_rgba(14,165,233,0)]",
      "hover:translate-y-[-3px]",
      "hover:shadow-[0_8px_24px_rgba(15,23,42,0.3),0_0_0_3px_rgba(14,165,233,0.15)]",
      "active:translate-y-[-1px]",
    ],
    // Play Store (Secondary) - white
    variant === "playstore" && [
      "bg-white text-[#0f172a]",
      "border-2 border-[rgba(15,23,42,0.1)]",
      "shadow-[0_4px_12px_rgba(0,0,0,0.08)]",
      "hover:translate-y-[-3px]",
      "hover:border-[rgba(15,23,42,0.2)]",
      "hover:shadow-[0_8px_24px_rgba(0,0,0,0.12),0_0_0_3px_rgba(14,165,233,0.1)]",
      "active:translate-y-[-1px]",
    ]
  );

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={cn(baseStyles, variantStyles, className)}
    >
      <Icon className="text-2xl w-6 h-6 flex-shrink-0" />
      {singleLine ? (
        <span className="font-bold">{label || labels.large}</span>
      ) : (
        <span className="flex flex-col items-start">
          <span className="text-[0.7rem] font-medium opacity-80 uppercase tracking-[0.03em]">
            {labels.small}
          </span>
          <span className="text-base font-bold leading-tight">
            {labels.large}
          </span>
        </span>
      )}
    </a>
  );
}

/**
 * A pair of download buttons (App Store + Play Store)
 * Convenience component for common use case
 */
interface DownloadButtonPairProps {
  size?: DownloadButtonSize;
  /** Layout direction */
  direction?: "row" | "column";
  /** Additional wrapper className */
  className?: string;
  /** Additional className for individual buttons */
  buttonClassName?: string;
}

export function DownloadButtonPair({
  size = "default",
  direction = "row",
  className,
  buttonClassName,
}: DownloadButtonPairProps) {
  return (
    <div
      className={cn(
        "flex gap-4",
        direction === "row" && "flex-col sm:flex-row",
        direction === "column" && "flex-col",
        className
      )}
    >
      <DownloadButton
        variant="appstore"
        size={size}
        className={cn("w-full sm:w-auto justify-center", buttonClassName)}
      />
      <DownloadButton
        variant="playstore"
        size={size}
        className={cn("w-full sm:w-auto justify-center", buttonClassName)}
      />
    </div>
  );
}

export default DownloadButton;
